import { NavLink } from "react-router-dom";
import { ClipboardList, CalendarDays, MessageCircle, Wallet, Archive, Settings, LogOut, Store, RefreshCw, FileSignature, ScrollText, ShieldCheck, UserCog, Boxes } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { ROLE_LABELS, can } from "@/lib/roles";

const NAV = [
  { to: "/", label: "Appuntamenti", icon: ClipboardList, id: "appuntamenti", end: true },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, id: "agenda" },
  { to: "/chat", label: "Chat", icon: MessageCircle, id: "chat" },
  { to: "/incassi", label: "Incassi", icon: Wallet, id: "incassi", perm: "cash" },
  { to: "/impegnative", label: "Impegnative", icon: FileSignature, id: "impegnative" },
  { to: "/magazzino", label: "Magazzino", icon: Boxes, id: "magazzino" },
  { to: "/archivio", label: "Archivio contratti", icon: Archive, id: "archivio", admin: true },
  { to: "/registro", label: "Registro attività", icon: ScrollText, id: "registro", admin: true },
  { to: "/privacy", label: "Privacy e backup", icon: ShieldCheck, id: "privacy", admin: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "impostazioni", admin: true },
];
const navFor = (user) => NAV.filter((n) => (!n.admin || user.role === "admin") && (!n.perm || can(user, n.perm)));

function Badge({ id }) {
  const { unread } = useChat();
  if (id !== "chat" || !unread) return null;
  return <span data-testid="chat-unread-badge" className="min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold grid place-items-center">{unread > 99 ? "99+" : unread}</span>;
}

function SideNav() {
  const { user } = useAuth();
  return (
    <nav className="flex flex-col gap-1">
      {navFor(user).map(({ to, label, icon: Icon, id, end }) => (
        <NavLink key={id} to={to} end={end} data-testid={`nav-${id}-link`}
          className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
          <Icon className="h-5 w-5" /> {label} <span className="ml-auto"><Badge id={id} /></span>
        </NavLink>
      ))}
    </nav>
  );
}

function BottomNav() {
  const { user } = useAuth();
  const items = navFor(user).filter((n) => !n.admin);
  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[var(--border)] grid pb-[env(safe-area-inset-bottom)]" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(({ to, label, icon: Icon, id, end }) => (
        <NavLink key={id} to={to} end={end} data-testid={`bottom-nav-${id}-tab`}
          className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "text-[var(--primary-hover)]" : "text-[var(--muted)]"}`}>
          <span className="relative"><Icon className="h-5 w-5" /><span className="absolute -top-2 -right-3"><Badge id={id} /></span></span> {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <aside className="no-print hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col justify-between gap-6 overflow-y-auto overscroll-contain border-r border-[var(--border)] bg-white p-5" data-testid="sidebar">
        <div>
          <div className="flex items-center gap-2.5 mb-10">
            <div className="h-10 w-10 rounded-xl bg-[var(--ink)] text-[var(--primary)] grid place-items-center"><Store className="h-5 w-5" /></div>
            <div>
              <p className="font-heading font-bold leading-tight">Mercatino</p>
              <p className="text-xs text-[var(--muted)]">Gestionale</p>
            </div>
          </div>
          <SideNav />
        </div>
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-xs text-[var(--accent)]"><RefreshCw className="h-3.5 w-3.5" /> Dati condivisi con il team</p>
          <NavLink to="/account" className="block rounded-xl bg-[var(--surface-2)] p-3 hover:bg-amber-50 transition-colors" data-testid="nav-account-link">
            <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name || user.username}</p>
            <p className="text-xs text-[var(--muted)] truncate flex items-center gap-1" data-testid="current-user-role"><UserCog className="h-3.5 w-3.5" /> {ROLE_LABELS[user.role]} · Il mio account</p>
          </NavLink>
          <button onClick={logout} data-testid="logout-button" className="nav-item w-full"><LogOut className="h-5 w-5" /> Esci</button>
        </div>
      </aside>
      <header className="no-print lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[var(--border)] px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2 shrink-0">
          <div className="h-8 w-8 rounded-lg bg-[var(--ink)] text-[var(--primary)] grid place-items-center"><Store className="h-4 w-4" /></div>
          <p className="font-heading font-bold hidden min-[400px]:block">Mercatino</p>
        </div>
        <div className="flex items-center">
          {navFor(user).filter((n) => n.admin).map(({ to, label, icon: Icon, id }) => (
            <NavLink key={id} to={to} aria-label={label} data-testid={`mobile-nav-${id}-link`}
              className={({ isActive }) => `p-2 rounded-lg ${isActive ? "text-[var(--primary-hover)] bg-amber-50" : "text-[var(--muted)]"}`}>
              <Icon className="h-5 w-5" />
            </NavLink>
          ))}
          <NavLink to="/account" aria-label="Il mio account" data-testid="mobile-nav-account-link"
            className={({ isActive }) => `p-2 rounded-lg ${isActive ? "text-[var(--primary-hover)] bg-amber-50" : "text-[var(--muted)]"}`}>
            <UserCog className="h-5 w-5" />
          </NavLink>
          <button onClick={logout} data-testid="mobile-logout-button" className="text-[var(--muted)] p-2" aria-label="Esci"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>
      <main className="lg:pl-64 pb-24 lg:pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">{children}</div>
      </main>
      <BottomNav />
    </div>
  );
}
